import {
  buildWhatsAppUrl, whatsappMessage, formatRupees,
  type Service, type SiteSettings,
} from '@cng/shared';

/**
 * Renders a service's price exactly as the original menu shows it.
 *
 * `displayPriceText` is the migrated string from the live site and always wins,
 * because prices there take many shapes — "₹599", "Starting from ₹5,999",
 * "₹499 / ₹699 / ₹799", "₹59 / ₹79 / ₹119 / -". It is only recomputed from the
 * structured fields when the owner edits a price in admin and leaves the display
 * text blank.
 */
export function priceLabel(service: Service): string {
  if (service.displayPriceText) return service.displayPriceText;

  switch (service.pricingType) {
    case 'starting-from':
      return service.basePrice != null ? `Starting from ${formatRupees(service.basePrice)}` : '';
    case 'range':
      return service.minPrice != null && service.maxPrice != null
        ? `${formatRupees(service.minPrice)} – ${formatRupees(service.maxPrice)}`
        : '';
    case 'variants':
      return (service.variants ?? [])
        .map((v) => (v.available && v.price != null ? formatRupees(v.price) : '-'))
        .join(' / ');
    case 'text':
      return '';
    case 'fixed':
    default:
      return service.basePrice != null ? formatRupees(service.basePrice) : '';
  }
}

/** The sub-label above variant prices, e.g. "Shoulder / Below Shoulder / Extra Long". */
export function variantLabel(service: Service): string {
  if (service.variantLabel) return service.variantLabel;
  if (service.pricingType !== 'variants') return '';
  return (service.variants ?? []).map((v) => v.label || v.name).join(' / ');
}

interface ServiceRowProps {
  service: Service;
  settings: SiteSettings;
  /** Booking links are opt-in so the default matches the original menu. */
  showBooking?: boolean;
}

export function ServiceRow({ service, settings, showBooking = false }: ServiceRowProps) {
  const price = priceLabel(service);
  const variants = variantLabel(service);
  const description = service.shortDescription || service.description;

  const bookHref = showBooking
    ? buildWhatsAppUrl({
      phone: settings.whatsappNumber || settings.primaryPhone,
      message: whatsappMessage('serviceBooking', { name: service.name }, settings.whatsappTemplates),
    })
    : '';

  return (
    <div className="service-row search-item">
      <div className="service-info">
        <span className="service-name">{service.name}</span>
        {/*
          The original menu carries a single sub-line: a description for a plain
          service, or the variant labels for a multi-price one. Rows that somehow
          have both get two lines rather than losing information.
        */}
        {description ? <div className="service-desc">{description}</div> : null}
        {variants ? <div className="service-desc">{variants}</div> : null}
      </div>
      <div className="service-price">
        {price}
        {showBooking && bookHref ? (
          <a
            className="service-book"
            href={bookHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Book ${service.name} on WhatsApp`}
          >
            Book
          </a>
        ) : null}
      </div>
    </div>
  );
}
